import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ShieldCheck, Clock, MonitorSmartphone } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";

const Account = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    if (!user) return null;

    const handleLogout = async () => {
        await logout();
        toast.success("Logged out.");
        navigate("/");
    };

    const history = [...(user.login_history || [])].reverse();

    return (
        <>
            <Seo title="My Account | Coaching Crafters AI" description="Your Coaching Crafters AI account." path="/account" />
            <section className="mx-auto max-w-5xl px-6 py-16 sm:px-10" data-testid="account-page">
                <Reveal>
                    <div className="flex flex-wrap items-center justify-between gap-6">
                        <div className="flex items-center gap-4">
                            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-ember text-lg font-bold text-bone">
                                {user.name?.[0]?.toUpperCase() || "U"}
                            </span>
                            <div>
                                <h1 className="font-serif text-3xl font-semibold text-ink" data-testid="account-name">
                                    {user.name}
                                </h1>
                                <p className="text-sm text-ink/55">{user.email}</p>
                            </div>
                        </div>
                        <button
                            data-testid="account-logout-button"
                            onClick={handleLogout}
                            className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-6 py-3 text-sm font-semibold text-ink transition-colors hover:border-ember hover:text-ember"
                        >
                            <LogOut className="h-4 w-4" /> Log out
                        </button>
                    </div>
                </Reveal>

                <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
                    {[
                        { label: "Role", value: user.role },
                        { label: "Licence", value: user.licence || "trial" },
                        { label: "Account status", value: user.account_status || "active" },
                    ].map((item) => (
                        <Reveal key={item.label}>
                            <div className="rounded-3xl border border-[#E2E8F4] bg-white p-6 shadow-sm">
                                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/40">{item.label}</p>
                                <p className="mt-2 text-lg font-semibold capitalize text-ink" data-testid={`account-${item.label.replace(" ", "-")}`}>
                                    {item.value}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal className="mt-10">
                    <div className="rounded-3xl border border-[#E2E8F4] bg-white p-8 shadow-sm">
                        <div className="flex items-center gap-3">
                            <ShieldCheck className="h-5 w-5 text-ember" />
                            <h2 className="font-serif text-xl font-semibold text-ink">Login history</h2>
                        </div>
                        <p className="mt-2 text-sm text-ink/55">
                            Every sign-in is recorded with device and IP, as described in Our Policy. If you spot
                            something you don't recognise, write to support@coachingcraftersai.com immediately.
                        </p>
                        {history.length === 0 ? (
                            <p className="mt-6 text-sm text-ink/45" data-testid="login-history-empty">
                                No previous logins recorded yet.
                            </p>
                        ) : (
                            <ul className="mt-6 divide-y divide-[#E2E8F4]" data-testid="login-history-table">
                                {history.map((h, i) => (
                                    <li key={i} className="flex flex-col gap-1 py-3.5 text-sm sm:flex-row sm:items-center sm:justify-between">
                                        <span className="flex items-center gap-2 text-ink/75">
                                            <Clock className="h-4 w-4 text-ember" />
                                            {new Date(h.at).toLocaleString("en-IN")}
                                        </span>
                                        <span className="flex items-center gap-2 text-xs text-ink/45">
                                            <MonitorSmartphone className="h-4 w-4" />
                                            {h.ip} · {(h.device || "").slice(0, 60)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </Reveal>
            </section>
        </>
    );
};

export default Account;
